import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  CpuChipIcon,
  DocumentTextIcon,
  InformationCircleIcon,
  LockClosedIcon,
  ShieldCheckIcon,
  SparklesIcon,
  TicketIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import AIReadinessAssessment from '../../components/AIReadinessAssessment';
import AdvancedReportPreview from './AdvancedReportPreview';
import AdvancedAssessmentWorkspace from './AdvancedAssessmentWorkspace';
import AssessmentCommerceStatus from './AssessmentCommerceStatus';
import { trackAdvancedBeginCheckout, trackAdvancedPurchase } from './assessmentAnalytics';
import { trackLeadConversion } from '../../utils/analytics';
import { PageHeroTitle } from '../../styles/typography';
import { ADVANCED_ASSESSMENT_PRICE } from './advancedAssessmentPricing';
import {
  AdvancedCommerceState,
  AdvancedEvidence,
  AdvancedOfferStatus,
  createAdvancedCheckout,
  createAdvancedSession,
  extractAdvancedEvidence,
  getAdvancedCommerceStatus,
  getAdvancedOfferStatus,
  getAdvancedSession,
  requestAdvancedRecovery,
  reviewAdvancedEvidence,
  saveAdvancedResponse,
  validateRedemptionCode
} from './advancedAssessmentApi';

type AssessmentTier = 'simple' | 'advanced';
type AssessmentView = 'choose' | 'simple' | 'overview' | 'commerce' | 'interview' | 'evidence' | 'report';

const Experience = styled.div`
  min-height: calc(100vh - 90px);
  background: var(--color-primary-light);
`;

const Hero = styled.header`
  position: relative;
  display: flex;
  min-height: 560px;
  align-items: center;
  overflow: hidden;
  padding: clamp(64px, 8vw, 104px) 20px;
  background: white;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 1;
    background: radial-gradient(ellipse at 30% 50%, rgba(11, 152, 206, 0.08) 0%, transparent 70%);
    pointer-events: none;
  }

  @media (max-width: 820px) { min-height: auto; }
`;

const HeroBackgroundImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.4;
  filter: blur(4px);
  transform: scale(1.05);
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.82) 0%, rgba(245, 247, 250, 0.72) 100%);
`;

const HeroInner = styled.div`
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(380px, 0.9fr);
  gap: clamp(38px, 6vw, 80px);
  width: min(1200px, 100%);
  margin: 0 auto;
  align-items: center;

  @media (max-width: 820px) { grid-template-columns: 1fr; }
`;

const Eyebrow = styled.p`
  margin: 0 0 18px;
  color: var(--color-accent-primary);
  font-size: var(--fs-sm);
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.15em;
  text-transform: uppercase;
`;

const HeroTitle = styled(PageHeroTitle)`
  max-width: 700px;
  margin: 0;
`;

const HeroSummary = styled.div`
  padding: clamp(24px, 4vw, 36px);
  border: 3px solid rgba(11, 152, 206, 0.15);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 24px 60px rgba(20, 53, 98, 0.16);
  backdrop-filter: blur(10px);

  p {
    margin: 0 0 18px;
    color: var(--text-secondary);
    font-size: var(--fs-lg);
    line-height: 1.6;
  }

  small { color: var(--text-muted); line-height: 1.5; }
`;

const Main = styled.div`
  width: min(1200px, calc(100% - 40px));
  margin: 0 auto;
  padding: clamp(54px, 7vw, 82px) 0 96px;

  @media (max-width: 600px) { width: min(100% - 24px, 1180px); padding-top: 28px; }
`;

const ChoiceHeader = styled.div`
  display: grid;
  justify-items: center;
  gap: 20px;
  max-width: 820px;
  margin: 0 auto 30px;
  text-align: center;

  h2 { margin: 0 0 10px; color: var(--text-primary); font-size: var(--fs-3xl); letter-spacing: var(--ls-tight); }
  p { margin: 0; color: var(--text-secondary); line-height: 1.55; }
`;

const MethodPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 12px;
  border: 1px solid var(--border-default);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.82);
  box-shadow: var(--shadow-sm);
  color: var(--color-accent-dark);
  font-size: 0.82rem;
  font-weight: 700;
  white-space: nowrap;

  svg { width: 17px; color: var(--color-accent-secondary); }
`;

const TierFieldset = styled.fieldset`
  display: grid;
  justify-items: center;
  margin: 0;
  padding: 0;
  border: 0;

  legend {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
`;

const TierToggle = styled.div`
  display: inline-grid;
  grid-template-columns: repeat(2, minmax(170px, 1fr));
  width: min(520px, 100%);
  margin-bottom: 22px;
  padding: 5px;
  border: 1px solid var(--davinci-cloud);
  border-radius: 11px;
  background: white;
  box-shadow: var(--shadow-sm);

  @media (max-width: 430px) { grid-template-columns: 1fr; }
`;

const TierToggleLabel = styled.label<{ $selected: boolean }>`
  position: relative;
  display: block;
  margin: 0;
  cursor: pointer;
  letter-spacing: 0;
  text-transform: none;

  input {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
  }

  span {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-height: 52px;
    padding: 10px 14px;
    border: 1px solid ${props => props.$selected ? 'var(--davinci-blue)' : 'transparent'};
    border-radius: 7px;
    background: ${props => props.$selected ? 'var(--davinci-blue)' : 'white'};
    color: ${props => props.$selected ? 'white' : 'var(--davinci-midnight)'};
    font-size: 0.9rem;
    font-weight: 800;
  }

  small {
    color: ${props => props.$selected ? 'var(--davinci-beach)' : 'var(--davinci-stone)'};
    font-size: 0.72rem;
    font-weight: 700;
  }

  input:focus-visible + span {
    outline: 3px solid rgba(11, 152, 206, 0.32);
    outline-offset: 2px;
  }
`;

const TierPanel = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.3fr) minmax(280px, 0.7fr);
  min-height: 360px;
  overflow: hidden;
  width: 100%;
  border: 1px solid rgba(11, 152, 206, 0.14);
  border-radius: 18px;
  background: white;
  box-shadow: 0 22px 60px rgba(20, 53, 98, 0.14);

  @media (max-width: 780px) { grid-template-columns: 1fr; }
`;

const TierPanelMain = styled.div`
  padding: clamp(28px, 5vw, 50px);

  h3 {
    margin: 0 0 14px;
    color: var(--davinci-midnight);
    font-size: clamp(1.9rem, 4vw, 3.25rem);
    letter-spacing: -0.04em;
  }

  > p {
    max-width: 660px;
    margin: 0 0 30px;
    color: var(--davinci-grey);
    line-height: 1.65;
  }
`;

const TierKicker = styled.p`
  margin: 0 0 10px !important;
  color: var(--davinci-denim) !important;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.09em;
  text-transform: uppercase;
`;

// The .png this used to reference was replaced by AVIF/WebP variants and
// 404'd on every load. Plain background-image first as the fallback for
// engines without image-set(); the panel is never wider than half the
// modal, so the 1280w "tablet" variant is the largest worth serving.
const TIER_PANEL_TINT = 'linear-gradient(rgba(11, 152, 206, 0.9), rgba(20, 53, 98, 0.94))';

const TierPanelAside = styled.aside`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: clamp(26px, 4vw, 40px);
  background-image: ${TIER_PANEL_TINT}, url('/images/ai-services-bg-tablet.webp');
  background-image: ${TIER_PANEL_TINT}, image-set(
    url('/images/ai-services-bg-tablet.avif') type('image/avif'),
    url('/images/ai-services-bg-tablet.webp') type('image/webp')
  );
  background-position: center;
  background-size: cover;
  border-left: 1px solid rgba(255, 255, 255, 0.18);

  @media (max-width: 780px) { border-top: 1px solid rgba(255, 255, 255, 0.18); border-left: 0; }
`;

const PanelFacts = styled.dl`
  display: grid;
  gap: 18px;
  margin: 0;

  div { padding-bottom: 16px; border-bottom: 1px solid rgba(255, 255, 255, 0.22); }
  div:last-child { padding-bottom: 0; border-bottom: 0; }
  dt { margin-bottom: 5px; color: rgba(255, 255, 255, 0.72); font-size: 0.7rem; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; }
  dd { margin: 0; color: white; font-size: 1.04rem; font-weight: 800; }
`;

const BoundaryPreview = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  margin-top: 24px;
  border-top: 3px solid var(--davinci-blue);

  div { position: relative; padding: 20px 12px 0 0; }
  div::before {
    content: '';
    position: absolute;
    top: -7px;
    left: 0;
    width: 10px;
    height: 10px;
    border: 2px solid white;
    border-radius: 50%;
    background: var(--davinci-blue);
  }
  strong { display: block; color: var(--davinci-midnight); font-size: 0.78rem; }
  span { color: var(--davinci-stone); font-size: 0.7rem; line-height: 1.35; }
`;

const TierList = styled.ul<{ $inverse?: boolean }>`
  display: grid;
  gap: 12px;
  margin: auto 0 0;
  padding: 0;
  list-style: none;

  li { display: flex; gap: 10px; color: ${props => props.$inverse ? 'rgba(255,255,255,0.82)' : 'var(--text-secondary)'}; line-height: 1.45; }
  svg { flex: 0 0 19px; width: 19px; color: ${props => props.$inverse ? 'var(--color-accent-light)' : 'var(--color-accent-secondary)'}; }
`;

const ActionBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-lg);
  margin-top: 22px;
  padding: 20px 22px;
  border: 1px solid var(--border-default);
  border-radius: 16px;
  background: white;
  box-shadow: var(--shadow-sm);

  p { margin: 0; color: var(--text-secondary); line-height: 1.5; }
  strong { color: var(--color-accent-dark); }

  @media (max-width: 680px) { align-items: stretch; flex-direction: column; }
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 52px;
  padding: 13px 24px;
  border: 0;
  border-radius: 8px;
  background: var(--color-accent-primary-fill);
  color: white;
  font: inherit;
  font-weight: 800;
  cursor: pointer;
  white-space: nowrap;
  box-shadow: var(--shadow-sm);
  transition: transform var(--transition-base), box-shadow var(--transition-base), background-color var(--transition-base);

  svg { width: 19px; }
  &:hover { transform: translateY(-2px); background: var(--color-accent-secondary); box-shadow: var(--shadow-md); }
  &:active { transform: translateY(0); }
  &:focus-visible { outline: 3px solid rgba(11, 152, 206, 0.34); outline-offset: 3px; }
  &:disabled { cursor: not-allowed; opacity: 0.55; transform: none; box-shadow: none; }
`;

const SecondaryButton = styled(PrimaryButton)`
  border: 1px solid var(--border-default);
  background: white;
  color: var(--color-accent-dark);

  &:hover { border-color: var(--color-accent-secondary); background: var(--color-secondary-lightest); }
`;

const BackButton = styled(SecondaryButton)`
  min-height: 44px;
  padding: 9px 13px;
`;

const ContextNote = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
  padding: 18px;
  border-left: 3px solid var(--color-accent-light);
  border-radius: 12px;
  background: rgba(255,255,255,0.82);
  color: var(--text-secondary);
  line-height: 1.6;

  svg { flex: 0 0 21px; width: 21px; color: var(--color-accent-secondary); }
`;

const Workspace = styled.section`
  width: min(1060px, 100%);
  margin: 0 auto;
`;

const WorkspaceHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--spacing-lg);
  margin-bottom: 26px;

  h1 { margin: 18px 0 10px; color: var(--color-accent-dark); font-size: clamp(1.9rem, 5vw, 3.3rem); letter-spacing: -0.045em; }
  p { max-width: 720px; margin: 0; color: var(--text-secondary); line-height: 1.65; }

  @media (max-width: 700px) { flex-direction: column; }
`;

const PreviewBadge = styled.span`
  display: inline-flex;
  padding: 8px 10px;
  border: 1px solid rgba(11, 152, 206, 0.25);
  border-radius: 999px;
  background: rgba(11, 152, 206, 0.07);
  color: var(--color-accent-secondary);
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const Panel = styled.div`
  padding: clamp(22px, 4vw, 36px);
  border: 1px solid var(--border-default);
  border-radius: 12px;
  background: white;
  box-shadow: 0 10px 30px rgba(20, 53, 98, 0.07);
`;

const OverviewGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.3fr) minmax(280px, 0.7fr);
  gap: 18px;

  @media (max-width: 820px) { grid-template-columns: 1fr; }
`;

const ProcessList = styled.ol`
  display: grid;
  gap: 0;
  margin: 0;
  padding: 0;
  list-style: none;
  counter-reset: process;

  li {
    position: relative;
    min-height: 92px;
    padding: 0 0 24px 58px;
    border-left: 1px solid var(--border-default);
    counter-increment: process;
  }

  li:last-child { border-left-color: transparent; padding-bottom: 0; }
  li::before {
    content: counter(process, decimal-leading-zero);
    position: absolute;
    left: -18px;
    top: -3px;
    display: grid;
    place-items: center;
    width: 36px;
    height: 36px;
    border: 1px solid var(--color-accent-secondary);
    border-radius: 50%;
    background: white;
    color: var(--color-accent-secondary);
    font-size: 0.73rem;
    font-weight: 800;
  }

  strong { display: block; margin-bottom: 6px; color: var(--color-accent-dark); }
  span { color: var(--text-secondary); line-height: 1.55; }
`;

const PricePanel = styled(Panel)`
  background: var(--color-accent-dark);
  color: white;

  h3 { margin: 0 0 6px; color: white; font-size: 2.5rem; }
  > p { margin: 0 0 24px; color: rgba(255,255,255,0.7); line-height: 1.5; }
`;

const StandardPrice = styled.p`
  margin: 0 0 5px !important;
  color: rgba(255,255,255,0.62) !important;
  font-size: 0.85rem;
  text-decoration: line-through;
`;

const OfferRefreshButton = styled.button`
  display: inline-flex;
  align-items: center;
  width: fit-content;
  min-height: 42px;
  margin: -8px 0 24px;
  padding: 9px 13px;
  border: 1px solid rgba(143, 215, 229, 0.5);
  border-radius: 8px;
  background: rgba(143, 215, 229, 0.08);
  color: white;
  font: inherit;
  font-size: 0.9rem;
  font-weight: 800;
  cursor: pointer;
  transition: background-color var(--transition-base), border-color var(--transition-base);

  &:hover { border-color: var(--color-accent-light); background: rgba(143, 215, 229, 0.16); }
  &:focus-visible { outline: 3px solid rgba(143, 215, 229, 0.48); outline-offset: 3px; }
  &:disabled { cursor: wait; opacity: 0.58; }
`;

/* Said once, where a price is shown. Repeating the currency on every figure is noise. */
const PriceNote = styled.p`
  margin: 0.25rem 0 0.75rem;
  font-size: 0.75rem;
  color: rgba(255,255,255,0.62);
`;

const CreditBox = styled.div`
  margin-bottom: 24px;
  padding: 16px;
  border: 1px solid rgba(143, 215, 229, 0.35);
  background: rgba(143, 215, 229, 0.08);

  strong { display: block; margin-bottom: 5px; color: white; }
  span { color: rgba(255,255,255,0.72); font-size: 0.9rem; line-height: 1.45; }
`;

const CodeLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  color: rgba(255,255,255,0.8);
  font-size: 0.82rem;
  font-weight: 800;
`;

const CodeRow = styled.div`
  display: flex;
  gap: 8px;

  input {
    min-width: 0;
    min-height: 46px;
    flex: 1;
    padding: 11px 12px;
    border: 1px solid rgba(255,255,255,0.38);
    border-radius: 6px;
    background: white;
    color: var(--text-primary);
    font: inherit;
    text-transform: uppercase;
  }

  button {
    min-height: 46px;
    padding: 10px 14px;
    border: 1px solid rgba(255,255,255,0.38);
    border-radius: 6px;
    background: transparent;
    color: white;
    font: inherit;
    font-weight: 800;
    cursor: pointer;
  }

  input:focus-visible, button:focus-visible { outline: 3px solid rgba(143, 215, 229, 0.55); outline-offset: 2px; }
`;

const CodeStatus = styled.p`
  min-height: 42px;
  margin: 8px 0 0 !important;
  color: rgba(255,255,255,0.68) !important;
  font-size: 0.8rem;
`;

const AccessFields = styled.div`
  display: grid;
  gap: 10px;
  margin-bottom: 18px;

  label {
    display: grid;
    gap: 6px;
    color: rgba(255,255,255,0.82);
    font-size: 0.78rem;
    font-weight: 800;
  }

  input {
    width: 100%;
    min-height: 44px;
    padding: 10px 12px;
    border: 1px solid rgba(255,255,255,0.38);
    border-radius: 6px;
    background: white;
    color: var(--text-primary);
    font: inherit;
  }

  input:focus-visible { outline: 3px solid rgba(143, 215, 229, 0.55); outline-offset: 2px; }
`;

const ConsentRow = styled.label`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 10px;
  align-items: start;
  margin: 16px 0 0;
  color: rgba(255,255,255,0.76);
  font-size: 0.78rem;
  line-height: 1.45;

  input { width: 18px; height: 18px; margin-top: 2px; }
`;

/* The specifics behind the consent tick. Small, but present before the money changes hands
   rather than in a policy page nobody opens -- informed consent that requires a second click
   is not informed. */
const ConsentDetail = styled.div`
  margin-top: 0.6rem;
  font-size: 0.78rem;
  line-height: 1.5;
  color: var(--color-text-secondary, #494949);
  p { margin: 0 0 0.45rem; }
  p:last-child { margin-bottom: 0; }
`;

const FlowError = styled.div`
  margin: 16px 0 0;
  padding: 12px 14px;
  border: 1px solid rgba(180, 35, 24, 0.25);
  border-radius: 8px;
  background: rgba(254, 243, 242, 0.96);
  color: #8f1f17;
  font-size: 0.88rem;
  line-height: 1.45;
`;

const RecoveryForm = styled.form`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 10px;
  max-width: 760px;
  margin: 18px auto 0;
  padding: 18px;
  border: 1px solid var(--border-default);
  border-radius: 12px;
  background: white;

  label { grid-column: 1 / -1; color: var(--text-primary); font-weight: 800; }
  input { min-height: 48px; padding: 10px 12px; border: 1px solid var(--border-dark); border-radius: 8px; font: inherit; }
  button { min-height: 48px; padding: 10px 16px; border: 1px solid var(--color-accent-primary); border-radius: 8px; background: var(--color-accent-primary-fill); color: white; font: inherit; font-weight: 800; cursor: pointer; }
  @media (max-width: 560px) { grid-template-columns: 1fr; }
`;

const RoleStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-top: 18px;

  @media (max-width: 650px) { grid-template-columns: 1fr; }
`;

const RoleCard = styled.div<{ $required?: boolean }>`
  padding: 17px;
  border: 1px solid var(--border-default);
  border-radius: 8px;
  background: var(--color-secondary-lightest);

  svg { width: 23px; color: var(--color-accent-secondary); }
  strong { display: block; margin: 12px 0 4px; color: var(--color-accent-dark); }
  span { color: var(--text-muted); font-size: 0.82rem; }
`;

const IntakeLayout = styled.div`
  display: grid;
  grid-template-columns: 230px minmax(0, 1fr);
  gap: 18px;

  @media (max-width: 800px) { grid-template-columns: 1fr; }
`;

const ProgressRail = styled.nav`
  align-self: start;
  padding: 20px;
  border: 1px solid var(--border-default);
  border-radius: 10px;
  background: white;

  strong { display: block; margin-bottom: 16px; color: var(--color-accent-dark); }
  ol { display: grid; gap: 12px; margin: 0; padding: 0; list-style: none; }
  li { display: flex; align-items: center; gap: 10px; color: var(--text-muted); font-size: 0.9rem; }
  li[aria-current="step"] { color: var(--color-accent-dark); font-weight: 800; }
  svg { width: 18px; }
`;

const FormHeader = styled.div`
  padding-bottom: 24px;
  border-bottom: 1px solid var(--border-light);

  span { color: var(--color-accent-secondary); font-size: 0.76rem; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; }
  h2 { margin: 10px 0 12px; color: var(--color-accent-dark); font-size: clamp(1.65rem, 4vw, 2.35rem); letter-spacing: -0.035em; }
  p { margin: 0; color: var(--text-secondary); line-height: 1.6; }
`;

const LongField = styled.div`
  margin-top: 28px;

  label { display: block; margin-bottom: 9px; color: var(--text-primary); font-weight: 800; }
  textarea {
    width: 100%;
    min-height: 240px;
    resize: vertical;
    padding: 17px;
    border: 1px solid var(--border-dark);
    border-radius: 8px;
    background: #fff;
    color: var(--text-primary);
    font: inherit;
    line-height: 1.65;
  }
  textarea:focus-visible { outline: 3px solid rgba(11, 152, 206, 0.25); border-color: var(--color-accent-secondary); }
`;

const FieldHint = styled.p`
  margin: 0 0 10px;
  color: var(--text-secondary);
  font-size: 0.9rem;
  line-height: 1.55;
`;

const FieldMeta = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 8px;
  color: var(--text-muted);
  font-size: 0.82rem;

  span:last-child { font-weight: 800; }
`;

const RoleToggle = styled.div`
  display: inline-flex;
  gap: 4px;
  margin: 24px 0 0;
  padding: 4px;
  border: 1px solid var(--border-default);
  border-radius: 8px;
  background: var(--color-secondary-lightest);

  button {
    min-height: 40px;
    padding: 8px 12px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: var(--text-secondary);
    font: inherit;
    cursor: pointer;
  }
  button[aria-pressed="true"] { background: white; color: var(--color-accent-dark); font-weight: 800; box-shadow: var(--shadow-sm); }
  button:focus-visible { outline: 3px solid rgba(11, 152, 206, 0.25); }
`;

const FormActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  margin-top: 28px;
  padding-top: 22px;
  border-top: 1px solid var(--border-light);

  @media (max-width: 520px) { align-items: stretch; flex-direction: column-reverse; }
`;

const EvidenceList = styled.div`
  display: grid;
  gap: 12px;
`;

const EvidenceCard = styled.article`
  display: grid;
  grid-template-columns: 92px minmax(0, 1fr) auto;
  gap: 18px;
  align-items: start;
  padding: 20px;
  border: 1px solid var(--border-default);
  border-radius: 8px;
  background: white;

  @media (max-width: 720px) { grid-template-columns: 1fr; gap: 9px; }
`;

const EvidenceId = styled.span`
  color: var(--color-accent-secondary);
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.08em;
`;

const EvidenceCopy = styled.div`
  strong { display: block; margin-bottom: 7px; color: var(--color-accent-dark); }
  p { margin: 0 0 8px; color: var(--text-secondary); line-height: 1.55; }
  small { color: var(--text-muted); }
`;

const EvidenceActions = styled.div`
  display: flex;
  gap: 7px;
  flex-wrap: wrap;

  button {
    min-height: 38px;
    padding: 7px 10px;
    border: 1px solid var(--border-default);
    border-radius: 6px;
    background: white;
    color: var(--davinci-midnight);
    font: inherit;
    font-size: 0.76rem;
    font-weight: 800;
    cursor: pointer;
  }

  button:hover { border-color: var(--davinci-blue); }
  button:focus-visible { outline: 3px solid rgba(11, 152, 206, 0.25); outline-offset: 2px; }
`;

const StatusTag = styled.span<{ $status: string }>`
  padding: 7px 9px;
  border: 1px solid ${props => props.$status === 'supported' ? 'var(--davinci-blue)' : props.$status === 'rejected' ? '#8f1f17' : 'var(--davinci-stone)'};
  border-radius: 999px;
  background: ${props => props.$status === 'supported' ? 'rgba(143, 215, 229, 0.22)' : props.$status === 'rejected' ? 'rgba(254, 243, 242, 0.96)' : 'var(--davinci-steam)'};
  color: var(--text-primary);
  font-size: 0.74rem;
  font-weight: 800;
  white-space: nowrap;
`;

const SimpleShell = styled.section`
  width: min(920px, 100%);
  margin: 0 auto;
`;

const SimpleFrame = styled.div`
  overflow: hidden;
  margin-top: 18px;
  border: 1px solid var(--border-default);
  border-radius: 12px;
  background: white;
  box-shadow: 0 12px 35px rgba(20, 53, 98, 0.08);
`;

const countWords = (value: string) => value.trim() ? value.trim().split(/\s+/).length : 0;
const AssessmentExperience: React.FC = () => {
  // `/assessment?tier=advanced` (linked from the Quick Score report) opens with the
  // Advanced option selected; the visitor still confirms it from the chooser.
  const [tier, setTier] = useState<AssessmentTier>(() =>
    new URLSearchParams(window.location.search).get('tier') === 'advanced' ? 'advanced' : 'simple');
  const [view, setView] = useState<AssessmentView>('choose');
  const [role, setRole] = useState<'executive' | 'it'>('executive');
  const [executiveResponse, setExecutiveResponse] = useState('');
  const [itResponse, setItResponse] = useState('');
  const [redemptionCode, setRedemptionCode] = useState('');
  const [codeStatus, setCodeStatus] = useState('');
  const [complimentaryAccess, setComplimentaryAccess] = useState(false);
  const [offerStatus, setOfferStatus] = useState<AdvancedOfferStatus | null>(null);
  const [offerError, setOfferError] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [executiveName, setExecutiveName] = useState('');
  const [executiveEmail, setExecutiveEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [executiveSaved, setExecutiveSaved] = useState(false);
  const [evidence, setEvidence] = useState<AdvancedEvidence[]>([]);
  const [isBusy, setIsBusy] = useState(false);
  const [flowError, setFlowError] = useState('');
  const [commerceState, setCommerceState] = useState<AdvancedCommerceState>('checkout');
  const [supportReference, setSupportReference] = useState('');
  const [manualCheckAvailable, setManualCheckAvailable] = useState(true);
  const [recoveryOpen, setRecoveryOpen] = useState(false);
  const [recoveryMessage, setRecoveryMessage] = useState('');

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [view]);

  const refreshOffer = useCallback(async () => {
    setOfferError('');
    try {
      setOfferStatus(await getAdvancedOfferStatus());
    } catch (error) {
      setOfferStatus(null);
      setOfferError(error instanceof Error ? error.message : 'Secure checkout availability is temporarily unavailable.');
    }
  }, []);

  useEffect(() => {
    void refreshOffer();
  }, [refreshOffer]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const resumedSessionId = params.get('advanced_session');
    const checkoutReturn = params.get('checkout');
    if (!resumedSessionId) return undefined;

    let isActive = true;
    const resumeToken = sessionStorage.getItem(`davinci-assessment-${resumedSessionId}`);
    setTier('advanced');
    setView('commerce');
    setSessionId(resumedSessionId);

    if (!resumeToken) {
      setCommerceState(checkoutReturn === 'cancelled' ? 'cancelled' : 'processing');
      setRecoveryOpen(true);
      setFlowError('This browser no longer has the private key for that assessment. Request a private recovery link to continue on this device.');
      return undefined;
    }

    setAccessToken(resumeToken);
    if (checkoutReturn === 'success') setCommerceState('processing');
    if (checkoutReturn === 'cancelled') setCommerceState('cancelled');
    setIsBusy(true);
    getAdvancedCommerceStatus(resumedSessionId, resumeToken)
      .then(async commerce => {
        if (!isActive) return;
        setCommerceState(current => {
          if (commerce.state !== 'checkout') return commerce.state;
          if (checkoutReturn === 'success') return 'processing';
          if (checkoutReturn === 'cancelled') return 'cancelled';
          return current === 'processing' ? 'processing' : commerce.state;
        });
        setSupportReference(commerce.supportReference || '');
        if (commerce.state !== 'paid') return;
        const result = await getAdvancedSession(resumedSessionId, resumeToken);
        if (!isActive) return;
        setOrganizationName(result.session.organizationName);
        setExecutiveName(result.session.executiveName);
        setExecutiveEmail(result.session.executiveEmail);
        setEvidence(result.evidence);

        const savedExecutive = result.responses.find(item => item.questionKey === 'executive_workflow');
        const savedIt = result.responses.find(item => item.questionKey === 'it_systems');
        setExecutiveResponse(savedExecutive?.response || '');
        setItResponse(savedIt?.response || '');
        setExecutiveSaved(Boolean(savedExecutive));

        setRole(savedExecutive && !savedIt ? 'it' : 'executive');
        setView('interview');
      })
      .catch(error => {
        if (isActive) setFlowError(error instanceof Error ? error.message : 'This assessment could not be resumed.');
      })
      .finally(() => {
        if (isActive) setIsBusy(false);
      });

    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    if (view !== 'commerce' || commerceState !== 'processing' || !sessionId || !accessToken) return undefined;
    let attempts = 0;
    const interval = window.setInterval(async () => {
      attempts += 1;
      try {
        const status = await getAdvancedCommerceStatus(sessionId, accessToken);
        setCommerceState(current => current === 'processing' && status.state === 'checkout'
          ? 'processing'
          : status.state);
        setSupportReference(status.supportReference || '');
      } catch {
        // The visible status remains actionable; polling errors must not overwrite a financial state.
      } finally {
        if (attempts === 60) window.clearInterval(interval);
      }
    }, 2000);
    return () => window.clearInterval(interval);
  }, [accessToken, commerceState, sessionId, view]);

  const activeResponse = role === 'executive' ? executiveResponse : itResponse;
  const activeWordCount = useMemo(() => countWords(activeResponse), [activeResponse]);

  const updateResponse = (value: string) => {
    if (countWords(value) > 500) return;
    if (role === 'executive') setExecutiveResponse(value);
    else setItResponse(value);
  };

  const applyRedemptionCode = async () => {
    const normalizedCode = redemptionCode.trim().toUpperCase();
    if (!normalizedCode) {
      setCodeStatus('Enter a code first.');
      return;
    }
    setIsBusy(true);
    setFlowError('');
    try {
      await validateRedemptionCode(normalizedCode);
      setRedemptionCode(normalizedCode);
      setComplimentaryAccess(true);
      setCodeStatus('Complimentary access — $0 — no project credit.');
    } catch (error) {
      setComplimentaryAccess(false);
      setCodeStatus(error instanceof Error ? error.message : 'That code is not valid.');
    } finally {
      setIsBusy(false);
    }
  };

  const startAdvancedSession = async () => {
    if (sessionId && accessToken) {
      // A timed-out checkout must retry its original order, not create another purchase.
      await retryCheckout();
      return;
    }
    if (!organizationName.trim() || !executiveName.trim() || !executiveEmail.trim() || !consent) {
      setFlowError('Enter your organization, name and work email, then confirm the processing consent.');
      return;
    }
    setFlowError('');
    setIsBusy(true);
    try {
      const result = await createAdvancedSession({
        organizationName,
        executiveName,
        executiveEmail,
        redemptionCode: redemptionCode.trim().toUpperCase() || undefined,
        consent
      });
      setSessionId(result.session.id);
      // A named, consented work email is a lead whether or not payment follows, so
      // GA4 gets generate_lead now. The Google Ads conversion is withheld: an
      // abandoned checkout must not train bidding.
      trackLeadConversion('advanced_assessment', { ads: false });
      window.history.replaceState(null, '', `/assessment?advanced_session=${encodeURIComponent(result.session.id)}`);
      setAccessToken(result.accessToken);
      sessionStorage.setItem(`davinci-assessment-${result.session.id}`, result.accessToken);
      if (result.paymentRequired) {
        const checkout = await createAdvancedCheckout(result.session.id, result.accessToken);
        if (checkout.state === 'reservation_unavailable') {
          setCommerceState('reservation_unavailable');
          setView('commerce');
          return;
        }
        if (!checkout.checkoutUrl) throw new Error('Secure checkout did not return a destination.');
        trackAdvancedBeginCheckout(result.session.id, checkout.offer?.priceCad ?? displayedPriceCad);
        window.location.assign(checkout.checkoutUrl);
        return;
      }
      setRole('executive');
      setView('interview');
    } catch (error) {
      setFlowError(error instanceof Error ? error.message : 'The assessment could not be started.');
    } finally {
      setIsBusy(false);
    }
  };

  const checkCommerceStatus = async () => {
    if (!sessionId || !accessToken || !manualCheckAvailable) return;
    setIsBusy(true);
    setManualCheckAvailable(false);
    window.setTimeout(() => setManualCheckAvailable(true), 5000);
    try {
      const status = await getAdvancedCommerceStatus(sessionId, accessToken);
      setCommerceState(current => current === 'processing' && status.state === 'checkout'
        ? 'processing'
        : status.state);
      setSupportReference(status.supportReference || '');
    } catch (error) {
      setFlowError(error instanceof Error ? error.message : 'Payment status is temporarily unavailable.');
    } finally {
      setIsBusy(false);
    }
  };

  const retryCheckout = async () => {
    if (!sessionId || !accessToken) return;
    setIsBusy(true);
    setFlowError('');
    try {
      const checkout = await createAdvancedCheckout(sessionId, accessToken);
      setCommerceState(checkout.state);
      setSupportReference(checkout.supportReference || '');
      if (checkout.state === 'reservation_unavailable') {
        return;
      }
      if (checkout.state !== 'checkout') return;
      if (!checkout.checkoutUrl) throw new Error('Secure checkout did not return a destination.');
      trackAdvancedBeginCheckout(sessionId, checkout.offer?.priceCad ?? displayedPriceCad);
      window.location.assign(checkout.checkoutUrl);
    } catch (error) {
      setFlowError(error instanceof Error ? error.message : 'Secure checkout is temporarily unavailable.');
    } finally {
      setIsBusy(false);
    }
  };

  const sendRecovery = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!sessionId || !executiveEmail) return;
    setIsBusy(true);
    try {
      const result = await requestAdvancedRecovery({ sessionId, email: executiveEmail });
      setRecoveryMessage(`If this address matches the private session, a recovery link is on its way. You can request another in ${result.resendAvailableInSeconds} seconds.`);
      setRecoveryOpen(false);
    } catch (error) {
      setRecoveryMessage(error instanceof Error ? error.message : 'We could not send a recovery link.');
    } finally {
      setIsBusy(false);
    }
  };

  const saveResponseAndContinue = async () => {
    if (!sessionId || !accessToken) {
      setFlowError('Start a private assessment session first.');
      return;
    }
    setIsBusy(true);
    setFlowError('');
    try {
      await saveAdvancedResponse(sessionId, accessToken, {
        role,
        questionKey: role === 'executive' ? 'executive_workflow' : 'it_systems',
        response: activeResponse
      });
      if (role === 'executive') {
        setExecutiveSaved(true);
        setRole('it');
        return;
      }
      if (!executiveSaved) {
        setRole('executive');
        setFlowError('Add and save the executive response before processing evidence.');
        return;
      }
      const result = await extractAdvancedEvidence(sessionId, accessToken);
      setEvidence(result.evidence);
      setView('evidence');
    } catch (error) {
      setFlowError(error instanceof Error ? error.message : 'The response could not be processed.');
    } finally {
      setIsBusy(false);
    }
  };

  const decideEvidence = async (evidenceId: string, status: 'supported' | 'rejected') => {
    if (!sessionId || !accessToken) return;
    setIsBusy(true);
    setFlowError('');
    try {
      const updated = await reviewAdvancedEvidence(sessionId, accessToken, evidenceId, status);
      setEvidence(current => current.map(item => item.id === evidenceId ? updated : item));
    } catch (error) {
      setFlowError(error instanceof Error ? error.message : 'The evidence decision could not be saved.');
    } finally {
      setIsBusy(false);
    }
  };

  const startSelectedTier = () => setView(tier === 'simple' ? 'simple' : 'overview');
  const paidCheckoutAvailable = Boolean(offerStatus?.available);
  const displayedOfferTier = offerStatus?.tier ?? 'founding';
  const displayedPriceCad = offerStatus?.priceCad ?? ADVANCED_ASSESSMENT_PRICE.launchCad;

  // The returning buyer can go straight from "paid" to the interview without the
  // commerce card ever rendering, so the purchase is reported from here as well.
  useEffect(() => {
    if (commerceState === 'paid' && sessionId) trackAdvancedPurchase(sessionId);
  }, [commerceState, sessionId]);
  const returnToOptions = () => {
    setView('choose');
  };

  if (view === 'report') {
    return (
      <Experience>
        <Main data-clarity-mask="True">
          <AdvancedReportPreview onBack={() => setView('overview')} onExit={returnToOptions} />
        </Main>
      </Experience>
    );
  }

  if (view === 'simple') {
    return (
      <Experience>
        <Main data-clarity-mask="True">
          <SimpleShell>
            <BackButton type="button" onClick={returnToOptions}><ArrowLeftIcon aria-hidden="true" />Assessment options</BackButton>
            <WorkspaceHeader>
              <div>
                <h1>Quick Score</h1>
                <p>A free, ungated decision snapshot for one real workflow.</p>
              </div>
              <PreviewBadge>12 questions · 7–10 minutes</PreviewBadge>
            </WorkspaceHeader>
            <SimpleFrame><AIReadinessAssessment /></SimpleFrame>
          </SimpleShell>
        </Main>
      </Experience>
    );
  }

  if (view === 'commerce') {
    return (
      <Experience>
        <Main data-clarity-mask="True">
          <AssessmentCommerceStatus
            state={commerceState}
            sessionId={sessionId || undefined}
            supportReference={supportReference}
            checking={isBusy}
            canCheck={manualCheckAvailable && Boolean(accessToken)}
            onCheck={checkCommerceStatus}
            onCheckout={accessToken ? retryCheckout : undefined}
            onResume={() => window.location.assign(`/assessment?advanced_session=${sessionId}`)}
            onRecovery={() => setRecoveryOpen(current => !current)}
          />
          {(recoveryOpen || !accessToken) && (
            <RecoveryForm data-clarity-mask="True" onSubmit={sendRecovery} aria-label="Private assessment recovery">
              <label htmlFor="recovery-email">Work email used for this assessment</label>
              <input id="recovery-email" type="email" autoComplete="email" value={executiveEmail} onChange={(event) => setExecutiveEmail(event.target.value)} required />
              <button type="submit" disabled={isBusy}>Send recovery link</button>
            </RecoveryForm>
          )}
          {recoveryMessage && <FlowError role="status" aria-live="polite">{recoveryMessage}</FlowError>}
          {flowError && <FlowError role="alert">{flowError}</FlowError>}
        </Main>
      </Experience>
    );
  }

  if (view === 'overview') {
    return (
      <Experience>
        <Main data-clarity-mask="True">
          <Workspace>
            <BackButton type="button" onClick={returnToOptions}><ArrowLeftIcon aria-hidden="true" />Assessment options</BackButton>
            <WorkspaceHeader>
              <div>
                <h1>Decision-Ready Assessment</h1>
                <p>Understand where technology can improve your business, what is worth doing first, and what needs to be verified before investing.</p>
              </div>
              <PreviewBadge>Scope, deliverables and access</PreviewBadge>
            </WorkspaceHeader>

            <OverviewGrid>
              <Panel>
                <Eyebrow>How the assessment works</Eyebrow>
                <p>Plan several focused sections, with additional questions only for the areas that need a closer look. Bring your business goals, system names and a few recent examples. You can save and take a break at any point.</p>
                <ProcessList>
                  <li><strong>Describe your business and priorities</strong><span>Explain your goals, constraints, systems and the changes already underway.</span></li>
                  <li><strong>Explore twelve business areas</strong><span>Identify friction across operations, sales, finance, people and technology. “Not sure” is a valid answer.</span></li>
                  <li><strong>Go deeper where it matters</strong><span>Focused follow-up examines up to three areas your answers raise. Save progress and return when you need another person’s input.</span></li>
                  <li><strong>Receive a decision brief and discuss it</strong><span>Priorities, supporting evidence, cost assumptions, prerequisites and a sequenced action plan—plus a call to review the conclusions.</span></li>
                </ProcessList>
                <RoleStrip>
                  <RoleCard $required><UserGroupIcon aria-hidden="true" /><strong>Business owner or director</strong><span>Primary respondent · goals and operations</span></RoleCard>
                  <RoleCard><CpuChipIcon aria-hidden="true" /><strong>IT input</strong><span>Consult when needed · systems and controls</span></RoleCard>
                  <RoleCard><ClipboardDocumentCheckIcon aria-hidden="true" /><strong>People closest to the work</strong><span>Consult when needed · examples and exceptions</span></RoleCard>
                </RoleStrip>
                <OfferRefreshButton type="button" onClick={() => setView('report')}>View an illustrative report format</OfferRefreshButton>
                <p>The sample shows the format, not an assessment of your business. Your report may recommend conventional improvements, AI assistance, or no investment until prerequisites are met.</p>
              </Panel>

              <PricePanel>
                <Eyebrow style={{ color: 'var(--color-accent-light)' }}>{displayedOfferTier === 'standard' ? 'Advanced Assessment' : 'Launch offer'}</Eyebrow>
                <p role="status">Preview — not available to purchase.</p>
                {displayedOfferTier === 'founding' && <StandardPrice>{`Standard $${ADVANCED_ASSESSMENT_PRICE.standardCad}`}</StandardPrice>}
                <h3>{displayedOfferTier === 'founding' ? `Launch $${ADVANCED_ASSESSMENT_PRICE.launchCad}` : `$${ADVANCED_ASSESSMENT_PRICE.standardCad}`}</h3>
                {displayedOfferTier === 'founding' && <p>Limited launch pricing · plus applicable tax</p>}
                {displayedOfferTier === 'standard' && <p>One-time assessment · plus applicable tax</p>}
                {!offerStatus && <p>We confirm secure checkout availability before accepting payment.</p>}
                {offerStatus?.availabilityReason === 'founding_reservations_in_progress' && <p role="status">All launch-price checkout reservations are currently in progress. No $500 checkout will be offered until the launch cohort is settled.</p>}
                {offerStatus?.availabilityReason === 'checkout_paused' && <p role="status">The $99 launch offer is published, but secure checkout is not open yet. No payment can be submitted.</p>}
                {offerError && <p role="alert">{offerError}</p>}
                {(!offerStatus || !paidCheckoutAvailable) && !complimentaryAccess && <OfferRefreshButton type="button" onClick={refreshOffer} disabled={isBusy}>Check checkout availability</OfferRefreshButton>}
                <p>One organization, twelve business areas, up to three focused deep dives, a private PDF report and an included results-review call.</p>
                <PriceNote>Prices in Canadian dollars.</PriceNote>
                <CreditBox><strong>$99 first-project credit</strong><span>One use, expiring after 90 days. A full refund is available before assessment submission; after submission or report processing begins, refunds require review.</span></CreditBox>
                <AccessFields>
                  <label htmlFor="advanced-organization">Organization
                    <input id="advanced-organization" value={organizationName} onChange={(event) => setOrganizationName(event.target.value)} autoComplete="organization" />
                  </label>
                  <label htmlFor="advanced-name">Executive or director
                    <input id="advanced-name" value={executiveName} onChange={(event) => setExecutiveName(event.target.value)} autoComplete="name" />
                  </label>
                  <label htmlFor="advanced-email">Work email
                    <input id="advanced-email" type="email" value={executiveEmail} onChange={(event) => setExecutiveEmail(event.target.value)} autoComplete="email" />
                  </label>
                </AccessFields>
                <CodeLabel htmlFor="redemption-code">Have a sales redemption code?</CodeLabel>
                <CodeRow>
                  <input id="redemption-code" value={redemptionCode} onChange={(event) => {
                    setRedemptionCode(event.target.value);
                    setComplimentaryAccess(false);
                    setCodeStatus('');
                  }} placeholder="REDEMPTION CODE" autoComplete="off" />
                  <button type="button" onClick={applyRedemptionCode} disabled={isBusy}>Apply</button>
                </CodeRow>
                <CodeStatus aria-live="polite">{codeStatus}</CodeStatus>
                <ConsentRow htmlFor="advanced-consent">
                  <input id="advanced-consent" type="checkbox" checked={consent} onChange={(event) => setConsent(event.target.checked)} />
                  <span>I understand that Davinci will privately store these responses and process them with AI models to draft findings for human review. I will not enter credentials, payment details, or health or other regulated records, and I will describe people by role rather than by name.</span>
                </ConsentRow>
                <ConsentDetail>
                  <p><strong>What happens to what you write.</strong> Your answers are sent to
                  AI models through our provider, OpenRouter, under a routing policy that
                  denies data collection and requires zero retention — your content is not
                  kept by the model provider and is not used to train models.</p>
                  <p>Where we research your market, only your sector, region and size band
                  are sent to our search provider. Your name, your answers and your report
                  are never included in a search.</p>
                  <p>This processing takes place outside Canada. Davinci stores your
                  responses and your report so you can return to them.</p>
                </ConsentDetail>
              </PricePanel>
            </OverviewGrid>

            <ActionBar>
              <p><strong>Start your private assessment.</strong><br />Your exact total, including tax, is shown in Stripe before you pay. A valid sales code provides complimentary access. Answers are saved securely so you can take breaks.</p>
              <PrimaryButton type="button" onClick={startAdvancedSession} disabled={isBusy || (!complimentaryAccess && !paidCheckoutAvailable)}>{isBusy ? 'Starting…' : complimentaryAccess ? 'Start complimentary assessment' : paidCheckoutAvailable ? 'Continue to secure checkout' : 'Paid checkout unavailable'} <ArrowRightIcon aria-hidden="true" /></PrimaryButton>
            </ActionBar>
            {flowError && <FlowError role="alert">{flowError}</FlowError>}
          </Workspace>
        </Main>
      </Experience>
    );
  }

  if ((view === 'interview' || view === 'evidence') && sessionId && accessToken) {
    return <Experience><Main data-clarity-mask="True"><AdvancedAssessmentWorkspace
      sessionId={sessionId} accessToken={accessToken} email={executiveEmail} organization={organizationName}
    /></Main></Experience>;
  }

  if (view === 'interview') {
    return (
      <Experience>
        <Main data-clarity-mask="True">
          <Workspace>
            <WorkspaceHeader>
              <div>
                <PreviewBadge>Private assessment session</PreviewBadge>
                <h1>Describe the work in your own words.</h1>
                <p>Structured questions provide comparable evidence. A few focused narratives capture context, exceptions, and operating reality.</p>
              </div>
              <BackButton type="button" onClick={() => setView('overview')}><ArrowLeftIcon aria-hidden="true" />Overview</BackButton>
            </WorkspaceHeader>

            <IntakeLayout>
              <ProgressRail aria-label="Advanced assessment progress">
                <strong>Section progress</strong>
                <ol>
                  <li aria-current="step"><DocumentTextIcon aria-hidden="true" />Workflow evidence</li>
                  <li><CpuChipIcon aria-hidden="true" />IT verification</li>
                  <li><ShieldCheckIcon aria-hidden="true" />Controls</li>
                  <li><ClipboardDocumentCheckIcon aria-hidden="true" />Evidence review</li>
                </ol>
              </ProgressRail>

              <Panel>
                <FormHeader>
                  <span>Representative narrative question</span>
                  <h2>{role === 'executive' ? 'Where does this workflow slow down or become inconsistent?' : 'How does information move through the systems?'}</h2>
                  <p>{role === 'executive' ? 'Use one recent example. Include what happened, who was involved, what a normal case looks like, and what must remain under human authority.' : 'Describe inputs, systems, access, sensitive data, logging, rollback, and how employee corrections could be captured.'}</p>
                </FormHeader>

                <RoleToggle aria-label="Preview respondent role">
                  <button type="button" aria-pressed={role === 'executive'} onClick={() => setRole('executive')}>Executive view</button>
                  <button type="button" aria-pressed={role === 'it'} onClick={() => setRole('it')}>IT view</button>
                </RoleToggle>

                <LongField>
                  <label htmlFor="workflow-evidence">Your response</label>
                  <FieldHint id="workflow-evidence-help">Aim for 100–200 words. You may use up to 500. Do not include passwords, personal health information, or confidential client records.</FieldHint>
                  <textarea id="workflow-evidence" aria-describedby="workflow-evidence-help workflow-evidence-status" value={activeResponse} onChange={(event) => updateResponse(event.target.value)} />
                  <FieldMeta id="workflow-evidence-status" aria-live="polite">
                    <span>{role === 'executive' && executiveSaved ? 'Saved securely' : 'Not yet saved'}</span>
                    <span>{activeWordCount} / 500 words</span>
                  </FieldMeta>
                </LongField>

                <ContextNote><LockClosedIcon aria-hidden="true" /><span>Current safeguards include server-side persistence, purpose-specific consent, private session access, provider privacy controls, and no raw responses in analytics or email. Separate contributor invite links are planned next.</span></ContextNote>

                <FormActions>
                  <SecondaryButton type="button" onClick={() => setView('overview')}>Back</SecondaryButton>
                  <PrimaryButton type="button" onClick={saveResponseAndContinue} disabled={isBusy || activeWordCount < 20}>
                    {isBusy ? 'Working…' : role === 'executive' ? 'Save and continue to IT' : 'Save and process evidence'} <ArrowRightIcon aria-hidden="true" />
                  </PrimaryButton>
                </FormActions>
                {flowError && <FlowError role="alert">{flowError}</FlowError>}
              </Panel>
            </IntakeLayout>
          </Workspace>
        </Main>
      </Experience>
    );
  }

  if (view === 'evidence') {
    return (
      <Experience>
        <Main data-clarity-mask="True">
          <Workspace>
            <WorkspaceHeader>
              <div>
                <PreviewBadge>Source-linked evidence review</PreviewBadge>
                <h1>People confirm the evidence before it affects the report.</h1>
                <p>OpenRouter proposed these structured findings. They remain excluded from scoring and reporting until a person confirms or rejects them.</p>
              </div>
              <BackButton type="button" onClick={() => setView('interview')}><ArrowLeftIcon aria-hidden="true" />Edit responses</BackButton>
            </WorkspaceHeader>

            <EvidenceList>
              {evidence.map((item, index) => (
                <EvidenceCard key={item.id}>
                  <EvidenceId>EV-{String(index + 1).padStart(2, '0')}</EvidenceId>
                  <EvidenceCopy>
                    <strong>{item.construct.replace(/_/g, ' ')}</strong>
                    <p>{item.claim}</p>
                    <small>{item.role} · “{item.sourceQuote}” · {item.confidence} confidence</small>
                    {item.followUp && <small>Follow-up: {item.followUp}</small>}
                  </EvidenceCopy>
                  <div>
                    <StatusTag $status={item.status}>{item.status.replace(/_/g, ' ')}</StatusTag>
                    {item.status === 'needs_review' && (
                      <EvidenceActions>
                        <button type="button" onClick={() => decideEvidence(item.id, 'supported')} disabled={isBusy}>Confirm</button>
                        <button type="button" onClick={() => decideEvidence(item.id, 'rejected')} disabled={isBusy}>Reject</button>
                      </EvidenceActions>
                    )}
                  </div>
                </EvidenceCard>
              ))}
            </EvidenceList>

            <ContextNote><InformationCircleIcon aria-hidden="true" /><span>Every substantive report finding will retain respondent, role, question, source span, extraction version, confidence, contradiction state, and any human correction.</span></ContextNote>
            <ActionBar>
              <p><strong>Next: deterministic scoring and the branded report engine.</strong><br />The current template remains illustrative until every finding is reviewed and server-owned scoring is connected.</p>
              <PrimaryButton type="button" onClick={() => setView('report')} disabled={evidence.some(item => item.status === 'needs_review')}>Preview report format <ArrowRightIcon aria-hidden="true" /></PrimaryButton>
            </ActionBar>
            {flowError && <FlowError role="alert">{flowError}</FlowError>}
          </Workspace>
        </Main>
      </Experience>
    );
  }

  return (
    <Experience>
      <Hero>
        <picture>
          <source media="(max-width: 768px)" srcSet="/images/hero/hero-bg-mobile.avif" type="image/avif" />
          <source media="(max-width: 768px)" srcSet="/images/hero/hero-bg-mobile.webp" type="image/webp" />
          <source srcSet="/images/hero/hero-bg-cropped-first-frame.avif" type="image/avif" />
          <HeroBackgroundImage src="/images/hero/hero-bg-cropped-first-frame.webp" alt="" width={1920} height={1080} />
        </picture>
        <HeroOverlay />
        <HeroInner>
          <div>
            <Eyebrow>AI Opportunity &amp; Readiness Assessment</Eyebrow>
            <HeroTitle>Decide where AI can help your team <span className="accent">most.</span></HeroTitle>
          </div>
          <HeroSummary>
            <p>Screen one workflow with Quick Score, or explore your business with Advanced. Identify practical improvements, the evidence behind them, and what to do next.</p>
            <small>Strategy before technology · No confidential records required · Built for business and IT leaders</small>
            <BoundaryPreview aria-label="Workflow Boundary Map preview">
              <div><strong>AI prepares</strong><span>Interpret and draft</span></div>
              <div><strong>Code validates</strong><span>Apply known rules</span></div>
              <div><strong>People decide</strong><span>Correct and own exceptions</span></div>
            </BoundaryPreview>
          </HeroSummary>
        </HeroInner>
      </Hero>

      <Main data-clarity-mask="True">
        <ChoiceHeader>
          <div><h2>Choose the evidence depth you need.</h2><p id="assessment-tier-description">Quick Score screens one workflow. Advanced explores the business and helps you decide what deserves investment.</p></div>
          <MethodPill><SparklesIcon aria-hidden="true" />Human judgment stays authoritative</MethodPill>
        </ChoiceHeader>

        <TierFieldset aria-describedby="assessment-tier-description">
          <legend>Choose an assessment tier</legend>
          <TierToggle>
            <TierToggleLabel $selected={tier === 'simple'}>
              <input type="radio" name="assessment-tier" value="simple" checked={tier === 'simple'} onChange={() => setTier('simple')} />
              <span>Quick Score <small>Free</small></span>
            </TierToggleLabel>
            <TierToggleLabel $selected={tier === 'advanced'}>
              <input type="radio" name="assessment-tier" value="advanced" checked={tier === 'advanced'} onChange={() => setTier('advanced')} />
              <span>Advanced <small>{`$${displayedPriceCad}${displayedOfferTier === 'founding' ? ' launch' : ''}`}</small></span>
            </TierToggleLabel>
          </TierToggle>

          <TierPanel>
            <TierPanelMain>
              <TierKicker>{tier === 'simple' ? 'Directional · one respondent' : 'Business priorities · evidence and action plan'}</TierKicker>
              <h3>{tier === 'simple' ? 'Quick Score' : 'Advanced Assessment'}</h3>
              <p>{tier === 'simple'
                ? 'Assess one workflow and receive an immediate grade, separate opportunity and execution scores, a solution route, and a three-page report.'
                : 'Explore your business goals and twelve operating areas. Receive a private decision brief explaining the strongest opportunities, what to defer, cost assumptions and practical next steps, with a call to review the findings.'}</p>
              <TierList>
                {tier === 'simple' ? (
                  <>
                    <li><CheckCircleIcon aria-hidden="true" />AI-assisted, hybrid, deterministic, or human-led route</li>
                    <li><CheckCircleIcon aria-hidden="true" />Four-construct report card with methodology and limitations</li>
                    <li><CheckCircleIcon aria-hidden="true" />One practical next step, with no contact gate</li>
                  </>
                ) : (
                  <>
                    <li><CheckCircleIcon aria-hidden="true" />Recommendations tied to your answers and supporting sources</li>
                    <li><CheckCircleIcon aria-hidden="true" />Priorities, prerequisites, cost assumptions and a sequenced plan</li>
                    <li><TicketIcon aria-hidden="true" />Sales redemption codes and $99 first-project credit</li>
                  </>
                )}
              </TierList>
            </TierPanelMain>
            <TierPanelAside>
              <PanelFacts>
                <div><dt>Availability</dt><dd>{tier === 'simple' ? 'Available now · free' : 'Preview — not available to purchase'}</dd></div>
                <div><dt>Price</dt><dd>{tier === 'simple' ? 'Free' : `$${displayedPriceCad}${displayedOfferTier === 'founding' ? ' launch' : ''}`}</dd></div>
                <div><dt>Time</dt><dd>{tier === 'simple' ? '7–10 minutes' : 'Complete in sections'}</dd></div>
                <div><dt>People</dt><dd>{tier === 'simple' ? '1 respondent' : 'You, with input as needed'}</dd></div>
                <div><dt>Output</dt><dd>{tier === 'simple' ? '3-page snapshot' : 'Decision brief + readout call'}</dd></div>
              </PanelFacts>
            </TierPanelAside>
          </TierPanel>
        </TierFieldset>

        <ActionBar>
          <p><strong>{tier === 'simple' ? 'Start with one workflow.' : 'See exactly what Advanced collects and delivers.'}</strong><br />{tier === 'simple' ? 'You will see the result before deciding whether to share contact information.' : 'Review the scope, see what you receive, and check access before deciding to pay.'}</p>
          <PrimaryButton type="button" onClick={startSelectedTier}>{tier === 'simple' ? 'Start Quick Score' : 'Explore Advanced'} <ArrowRightIcon aria-hidden="true" /></PrimaryButton>
        </ActionBar>

        <ContextNote><ShieldCheckIcon aria-hidden="true" /><span>All prices in Canadian dollars. Both paths are decision aids, not certifications. Advanced explains its evidence, assumptions and unknowns. Estimates are planning inputs to verify, not promised savings.</span></ContextNote>
      </Main>
    </Experience>
  );
};

export default AssessmentExperience;
